import { authHeaders } from "../lib/http";
import { useState, useCallback } from "react";
import {
  Layers,
  Plus,
  Edit3,
  Trash2,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Upload,
  X,
} from "lucide-react";

const API = (import.meta.env.VITE_API_URL || "") + "/api";

interface EtsyListing {
  listing_id: number;
  title: string;
  state: string;
  price: { amount: number; divisor: number; currency_code: string };
  quantity: number;
  tags: string[];
}

interface OperationResult {
  total: number;
  success: number;
  failed: number;
  details: Array<{ id?: string; title?: string; status: "success" | "failed"; error?: string }>;
}

type Tab = "create" | "update" | "delete" | "renew";

interface BulkCreateRow {
  title: string;
  description: string;
  price: string;
  quantity: string;
  tags: string;
}

const EMPTY_ROW: BulkCreateRow = { title: "", description: "", price: "", quantity: "1", tags: "" };

const TABS: { key: Tab; label: string; icon: React.ElementType; color: string }[] = [
  { key: "create", label: "Create", icon: Plus, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  { key: "update", label: "Update", icon: Edit3, color: "text-blue-600 bg-blue-50 border-blue-200" },
  { key: "delete", label: "Delete", icon: Trash2, color: "text-red-600 bg-red-50 border-red-200" },
  { key: "renew", label: "Renew", icon: RefreshCw, color: "text-amber-600 bg-amber-50 border-amber-200" },
];

export default function BulkOperationsPage() {
  const [activeTab, setActiveTab] = useState<Tab>("create");

  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Layers className="h-6 w-6 text-indigo-500" />
          Bulk Operations
        </h1>
        <p className="mt-1 text-sm text-gray-500">Manage multiple Etsy listings at once</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 flex items-center gap-2 border-b border-gray-200 pb-3">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key ? `${tab.color} border` : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "create" && <BulkCreateTab />}
      {activeTab === "update" && <BulkUpdateTab />}
      {activeTab === "delete" && <BulkDeleteTab />}
      {activeTab === "renew" && <BulkRenewTab />}
    </div>
  );
}

/* ============= Results Display ============= */
function ResultsSummary({ result }: { result: OperationResult }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Operation Results</h3>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="rounded-lg bg-gray-50 p-3 text-center">
          <p className="text-xs text-gray-500">Total</p>
          <p className="text-lg font-bold text-gray-900">{result.total}</p>
        </div>
        <div className="rounded-lg bg-emerald-50 p-3 text-center">
          <p className="text-xs text-emerald-600">Success</p>
          <p className="text-lg font-bold text-emerald-700">{result.success}</p>
        </div>
        <div className="rounded-lg bg-red-50 p-3 text-center">
          <p className="text-xs text-red-600">Failed</p>
          <p className="text-lg font-bold text-red-700">{result.failed}</p>
        </div>
      </div>
      {result.details.length > 0 && (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {result.details.map((d, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${
                d.status === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
              }`}
            >
              {d.status === "success" ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
              <span className="truncate">{d.title ?? d.id ?? `Item ${i + 1}`}</span>
              {d.error && <span className="ml-auto text-red-500 flex-shrink-0">{d.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============= Progress Bar ============= */
function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = total > 0 ? (current / total) * 100 : 0;
  return (
    <div className="mb-4">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>Processing...</span>
        <span>{current}/{total}</span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full bg-indigo-500 transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/* ============= BULK CREATE ============= */
function BulkCreateTab() {
  const [mode, setMode] = useState<"csv" | "manual">("csv");
  const [csvText, setCsvText] = useState("");
  const [rows, setRows] = useState<BulkCreateRow[]>([{ ...EMPTY_ROW }]);
  const [preview, setPreview] = useState<BulkCreateRow[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [result, setResult] = useState<OperationResult | null>(null);

  function parseCsv(): BulkCreateRow[] {
    return csvText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split("|");
        return {
          title: parts[0]?.trim() || "",
          description: parts[1]?.trim() || "",
          price: parts[2]?.trim() || "",
          quantity: parts[3]?.trim() || "1",
          tags: parts[4]?.trim() || "",
        };
      });
  }

  function handlePreview() {
    const items = mode === "csv" ? parseCsv() : rows.filter((r) => r.title.trim());
    if (items.length === 0) {
      alert("No listings to create.");
      return;
    }
    setPreview(items);
  }

  function addRow() {
    setRows([...rows, { ...EMPTY_ROW }]);
  }

  function updateRow(index: number, field: keyof BulkCreateRow, value: string) {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
  }

  function removeRow(index: number) {
    if (rows.length <= 1) return;
    setRows(rows.filter((_, i) => i !== index));
  }

  async function handleSubmit() {
    if (!preview || preview.length === 0) return;
    setSubmitting(true);
    setProgress({ current: 0, total: preview.length });
    setResult(null);

    try {
      const listings = preview.map((r) => ({
        title: r.title,
        description: r.description,
        price: parseFloat(r.price) || 0,
        quantity: parseInt(r.quantity, 10) || 1,
        tags: r.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      }));

      const res = await fetch(`${API}/bulk-operations/create`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ listings }),
      });

      if (!res.ok) throw new Error("Bulk create failed");
      const data = await res.json();
      setProgress({ current: preview.length, total: preview.length });
      setResult(
        data.data ?? {
          total: listings.length,
          success: data.success ?? listings.length,
          failed: data.failed ?? 0,
          details: data.details ?? [],
        },
      );
      setPreview(null);
    } catch {
      setResult({ total: preview.length, success: 0, failed: preview.length, details: [{ status: "failed", error: "Request failed" }] });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => { setMode("csv"); setPreview(null); }}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
            mode === "csv" ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-100"
          }`}
        >
          CSV Input
        </button>
        <button
          onClick={() => { setMode("manual"); setPreview(null); }}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
            mode === "manual" ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-100"
          }`}
        >
          Manual Entry
        </button>
      </div>

      {/* CSV Mode */}
      {mode === "csv" && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-xs text-gray-500 mb-2">
            Each line: <code className="bg-gray-100 px-1 rounded">title|description|price|quantity|tags(comma-sep)</code>
          </p>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            rows={8}
            placeholder={"Custom Mug|Beautiful custom mug with name|19.99|10|mug,custom,gift\nPersonalized Tote|Canvas tote bag|24.99|5|tote,bag,personalized"}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
          />
        </div>
      )}

      {/* Manual Mode */}
      {mode === "manual" && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="space-y-3">
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  {i === 0 && <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>}
                  <input
                    type="text"
                    value={row.title}
                    onChange={(e) => updateRow(i, "title", e.target.value)}
                    placeholder="Title"
                    className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                </div>
                <div className="col-span-3">
                  {i === 0 && <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>}
                  <input
                    type="text"
                    value={row.description}
                    onChange={(e) => updateRow(i, "description", e.target.value)}
                    placeholder="Description"
                    className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                </div>
                <div className="col-span-1">
                  {i === 0 && <label className="block text-xs font-medium text-gray-500 mb-1">Price</label>}
                  <input
                    type="number"
                    step="0.01"
                    value={row.price}
                    onChange={(e) => updateRow(i, "price", e.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                </div>
                <div className="col-span-1">
                  {i === 0 && <label className="block text-xs font-medium text-gray-500 mb-1">Qty</label>}
                  <input
                    type="number"
                    min="1"
                    value={row.quantity}
                    onChange={(e) => updateRow(i, "quantity", e.target.value)}
                    placeholder="1"
                    className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                </div>
                <div className="col-span-3">
                  {i === 0 && <label className="block text-xs font-medium text-gray-500 mb-1">Tags (comma-sep)</label>}
                  <input
                    type="text"
                    value={row.tags}
                    onChange={(e) => updateRow(i, "tags", e.target.value)}
                    placeholder="tag1, tag2"
                    className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                </div>
                <div className="col-span-1 flex justify-center">
                  <button
                    onClick={() => removeRow(i)}
                    disabled={rows.length <= 1}
                    className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-30"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={addRow}
            className="mt-3 flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700"
          >
            <Plus className="h-4 w-4" />
            Add Row
          </button>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Preview ({preview.length} listings)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="py-2 px-2 text-left text-gray-500 font-medium">#</th>
                  <th className="py-2 px-2 text-left text-gray-500 font-medium">Title</th>
                  <th className="py-2 px-2 text-left text-gray-500 font-medium">Price</th>
                  <th className="py-2 px-2 text-left text-gray-500 font-medium">Qty</th>
                  <th className="py-2 px-2 text-left text-gray-500 font-medium">Tags</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-1.5 px-2 text-gray-400">{i + 1}</td>
                    <td className="py-1.5 px-2 text-gray-900 font-medium truncate max-w-[200px]">{r.title}</td>
                    <td className="py-1.5 px-2 text-gray-700">${r.price}</td>
                    <td className="py-1.5 px-2 text-gray-700">{r.quantity}</td>
                    <td className="py-1.5 px-2 text-gray-500 truncate max-w-[150px]">{r.tags}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Progress */}
      {submitting && <ProgressBar current={progress.current} total={progress.total} />}

      {/* Results */}
      {result && <ResultsSummary result={result} />}

      {/* Actions */}
      <div className="flex gap-3">
        {!preview && (
          <button
            onClick={handlePreview}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
          >
            Preview
          </button>
        )}
        {preview && (
          <>
            <button
              onClick={() => setPreview(null)}
              className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50"
            >
              Back to Edit
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Create {preview.length} Listings
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ============= BULK UPDATE ============= */
function BulkUpdateTab() {
  const [listings, setListings] = useState<EtsyListing[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [updateFields, setUpdateFields] = useState({ price: "", quantity: "", tags: "", tagsMode: "append", state: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<OperationResult | null>(null);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/etsy/listings?state=active&limit=100`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setListings(data.data?.results ?? data.results ?? []);
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, []);

  if (!fetched) fetchListings();

  function toggleSelect(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function toggleAll() {
    if (selected.size === listings.length) setSelected(new Set());
    else setSelected(new Set(listings.map((l) => l.listing_id)));
  }

  async function handleSubmit() {
    if (selected.size === 0) return;
    setSubmitting(true);
    setResult(null);
    try {
      const updates: Record<string, unknown> = {};
      if (updateFields.price) updates.price = parseFloat(updateFields.price);
      if (updateFields.quantity) updates.quantity = parseInt(updateFields.quantity, 10);
      if (updateFields.tags) {
        updates.tags = updateFields.tags.split(",").map((t) => t.trim()).filter(Boolean);
        updates.tagsMode = updateFields.tagsMode;
      }
      if (updateFields.state) updates.state = updateFields.state;

      const res = await fetch(`${API}/bulk-operations/update`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ listingIds: Array.from(selected), updates }),
      });
      if (!res.ok) throw new Error("Bulk update failed");
      const data = await res.json();
      setResult(data.data ?? { total: selected.size, success: selected.size, failed: 0, details: data.details ?? [] });
      setSelected(new Set());
      fetchListings();
    } catch {
      setResult({ total: selected.size, success: 0, failed: selected.size, details: [{ status: "failed", error: "Request failed" }] });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Update Fields */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Fields to Update</h3>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Price ($)</label>
            <input
              type="number"
              step="0.01"
              value={updateFields.price}
              onChange={(e) => setUpdateFields({ ...updateFields, price: e.target.value })}
              placeholder="Leave blank to skip"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Quantity</label>
            <input
              type="number"
              min="0"
              value={updateFields.quantity}
              onChange={(e) => setUpdateFields({ ...updateFields, quantity: e.target.value })}
              placeholder="Leave blank to skip"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Tags
              <select
                value={updateFields.tagsMode}
                onChange={(e) => setUpdateFields({ ...updateFields, tagsMode: e.target.value })}
                className="ml-2 text-xs border-none bg-transparent text-indigo-600 focus:outline-none"
              >
                <option value="append">Append</option>
                <option value="replace">Replace</option>
              </select>
            </label>
            <input
              type="text"
              value={updateFields.tags}
              onChange={(e) => setUpdateFields({ ...updateFields, tags: e.target.value })}
              placeholder="tag1, tag2"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">State</label>
            <select
              value={updateFields.state}
              onChange={(e) => setUpdateFields({ ...updateFields, state: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              <option value="">No change</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </div>

      {/* Listings */}
      <ListingSelector
        listings={listings}
        loading={loading}
        selected={selected}
        onToggle={toggleSelect}
        onToggleAll={toggleAll}
      />

      {result && <ResultsSummary result={result} />}

      <button
        onClick={handleSubmit}
        disabled={selected.size === 0 || submitting}
        className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Edit3 className="h-4 w-4" />}
        Update {selected.size} Listings
      </button>
    </div>
  );
}

/* ============= BULK DELETE ============= */
function BulkDeleteTab() {
  const [listings, setListings] = useState<EtsyListing[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<OperationResult | null>(null);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/etsy/listings?state=active&limit=100`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setListings(data.data?.results ?? data.results ?? []);
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, []);

  if (!fetched) fetchListings();

  function toggleSelect(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function toggleAll() {
    if (selected.size === listings.length) setSelected(new Set());
    else setSelected(new Set(listings.map((l) => l.listing_id)));
  }

  async function handleDelete() {
    setSubmitting(true);
    setShowConfirm(false);
    setResult(null);
    try {
      const res = await fetch(`${API}/bulk-operations/delete`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ listingIds: Array.from(selected) }),
      });
      if (!res.ok) throw new Error("Bulk delete failed");
      const data = await res.json();
      setResult(data.data ?? { total: selected.size, success: selected.size, failed: 0, details: data.details ?? [] });
      setSelected(new Set());
      fetchListings();
    } catch {
      setResult({ total: selected.size, success: 0, failed: selected.size, details: [{ status: "failed", error: "Request failed" }] });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
        <p className="text-xs text-red-700">Deleted listings cannot be recovered. Please select carefully.</p>
      </div>

      <ListingSelector
        listings={listings}
        loading={loading}
        selected={selected}
        onToggle={toggleSelect}
        onToggleAll={toggleAll}
      />

      {result && <ResultsSummary result={result} />}

      <button
        onClick={() => setShowConfirm(true)}
        disabled={selected.size === 0 || submitting}
        className="flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        Delete {selected.size} Listings
      </button>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowConfirm(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full m-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="mb-6 text-sm text-gray-700">
              You are about to delete <strong>{selected.size}</strong> listing{selected.size !== 1 ? "s" : ""}. Are you sure?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============= BULK RENEW ============= */
function BulkRenewTab() {
  const [listings, setListings] = useState<EtsyListing[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<OperationResult | null>(null);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch expired and inactive listings
      const [expiredRes, inactiveRes] = await Promise.all([
        fetch(`${API}/etsy/listings?state=expired&limit=100`, {
          headers: authHeaders(),
        }),
        fetch(`${API}/etsy/listings?state=inactive&limit=100`, {
          headers: authHeaders(),
        }),
      ]);
      const expiredData = expiredRes.ok ? await expiredRes.json() : { data: { results: [] } };
      const inactiveData = inactiveRes.ok ? await inactiveRes.json() : { data: { results: [] } };
      const expired = expiredData.data?.results ?? expiredData.results ?? [];
      const inactive = inactiveData.data?.results ?? inactiveData.results ?? [];
      setListings([...expired, ...inactive]);
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, []);

  if (!fetched) fetchListings();

  function toggleSelect(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function toggleAll() {
    if (selected.size === listings.length) setSelected(new Set());
    else setSelected(new Set(listings.map((l) => l.listing_id)));
  }

  async function handleRenew() {
    if (selected.size === 0) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/bulk-operations/renew`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ listingIds: Array.from(selected) }),
      });
      if (!res.ok) throw new Error("Bulk renew failed");
      const data = await res.json();
      setResult(data.data ?? { total: selected.size, success: selected.size, failed: 0, details: data.details ?? [] });
      setSelected(new Set());
      fetchListings();
    } catch {
      setResult({ total: selected.size, success: 0, failed: selected.size, details: [{ status: "failed", error: "Request failed" }] });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center gap-2">
        <RefreshCw className="h-4 w-4 text-amber-500 flex-shrink-0" />
        <p className="text-xs text-amber-700">
          Renewing a listing costs $0.20 per listing. Only expired and inactive listings are shown.
        </p>
      </div>

      <ListingSelector
        listings={listings}
        loading={loading}
        selected={selected}
        onToggle={toggleSelect}
        onToggleAll={toggleAll}
        emptyMessage="No expired or inactive listings found"
      />

      {result && <ResultsSummary result={result} />}

      <button
        onClick={handleRenew}
        disabled={selected.size === 0 || submitting}
        className="flex items-center gap-2 rounded-lg bg-amber-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        Renew {selected.size} Listings {selected.size > 0 && `($${(selected.size * 0.2).toFixed(2)})`}
      </button>
    </div>
  );
}

/* ============= SHARED LISTING SELECTOR ============= */
function ListingSelector({
  listings,
  loading,
  selected,
  onToggle,
  onToggleAll,
  emptyMessage = "No listings found",
}: {
  listings: EtsyListing[];
  loading: boolean;
  selected: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: () => void;
  emptyMessage?: string;
}) {
  function formatPrice(price: EtsyListing["price"]) {
    return `${(price.amount / price.divisor).toFixed(2)} ${price.currency_code}`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-12">
        <Layers className="mb-2 h-8 w-8 text-gray-300" />
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 bg-gray-50">
        <input
          type="checkbox"
          checked={selected.size === listings.length && listings.length > 0}
          onChange={onToggleAll}
          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-xs font-medium text-gray-500">
          {selected.size > 0 ? `${selected.size} of ${listings.length} selected` : `${listings.length} listings`}
        </span>
      </div>

      {/* Listings */}
      <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
        {listings.map((listing) => (
          <div
            key={listing.listing_id}
            className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
              selected.has(listing.listing_id) ? "bg-indigo-50" : ""
            }`}
            onClick={() => onToggle(listing.listing_id)}
          >
            <input
              type="checkbox"
              checked={selected.has(listing.listing_id)}
              onChange={() => onToggle(listing.listing_id)}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900 truncate">{listing.title}</p>
              <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                <span>{formatPrice(listing.price)}</span>
                <span>Qty: {listing.quantity}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                  listing.state === "active"
                    ? "bg-emerald-50 text-emerald-600"
                    : listing.state === "expired"
                      ? "bg-red-50 text-red-600"
                      : "bg-gray-100 text-gray-500"
                }`}>
                  {listing.state}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
